// Los dos enlaces directos de la ficha del cliente (Lissette, 2026-09-14):
// "Cotizar" y "Pedir firma". Desde la ficha se elige QUE se va a hacer (que
// tipo de cotizacion, que plantilla de firma) y la pantalla de destino se abre
// ya con ese cliente puesto, sin volver a buscarlo en un desplegable de mil
// nombres.
//
// POR QUE UNA CAJA DE PENDIENTES Y NO SOLO UN EVENTO. El patron que ya existia
// en la casa (ScheduledSends -> MassEmail) dispara un CustomEvent con el
// contenido y enseguida navega. Eso funciona solo si la pantalla de destino YA
// esta montada: Cotizaciones y Firmas se cargan bajo demanda
// (`bajoDemanda(() => import(...))` en deskte-full.jsx), asi que el evento sale
// ANTES de que su codigo exista y no lo escucha nadie — el usuario aterriza en
// la pantalla vacia y el clic se perdio sin un solo error.
//
// Aqui el dato se DEJA en un modulo (sobrevive al montaje) y el evento es solo
// un aviso para la pantalla que ya estaba abierta. La de destino lo recoge al
// montarse Y al oir el aviso, asi que da igual quien llegue primero.

import { tiposDeCotizacion } from './components/quoteCoverageFields.js';

/** Nombres de los pendientes. Uno por destino. */
export const PENDIENTE_COTIZAR = 'cotizar';
export const PENDIENTE_FIRMA = 'firma';
// No sale de la ficha del cliente: es el "Reenviar" de Correos Programados, que
// llevaba el asunto y el cuerpo a Email Masivo con un CustomEvent suelto y por
// tanto no llegaban nunca (Email Masivo tampoco esta montado cuando se dispara).
// Vive aqui para que haya UNA manera de pasarse un dato entre pantallas.
export const PENDIENTE_EMAIL_MASIVO = 'email-masivo';
// La entrevista guiada del cotizador (docs/PLAN-cotizador-guiado.md, Fase 2):
// sale del mismo "Cotizar" de la ficha y abre Cotizaciones → Entrevistas.
export const PENDIENTE_ENTREVISTA = 'entrevista';
// "Inscribir en HealthSherpa" desde la ficha (Lissette, 2026-10-28): abre la
// pantalla de inscripción con el cliente ya elegido.
export const PENDIENTE_HEALTHSHERPA = 'healthsherpa';

/** El aviso para una pantalla que ya estaba montada. */
export const EVENTO_PENDIENTE = 'deskte:pendiente';

const pendientes = new Map();

/**
 * Deja un pendiente para la pantalla `nombre` y avisa.
 * Solo hay uno por nombre: el ultimo clic manda.
 */
export function dejarPendiente(nombre, datos) {
  pendientes.set(nombre, datos);
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(EVENTO_PENDIENTE, { detail: { nombre } }));
  }
}

/**
 * Mira el pendiente SIN consumirlo. La pantalla de destino a veces todavia no
 * puede usarlo (la lista de plantillas aun no llego del servidor); si lo
 * consumiera en ese momento el clic se perderia igual que con el evento suelto.
 */
export function leerPendiente(nombre) {
  return pendientes.has(nombre) ? pendientes.get(nombre) : null;
}

/** Lo lee y lo borra. Un pendiente se aplica una sola vez. */
export function tomarPendiente(nombre) {
  const datos = leerPendiente(nombre);
  pendientes.delete(nombre);
  return datos;
}

/** Para las pruebas y para cancelar un pendiente que ya no aplica. */
export function olvidarPendientes() {
  pendientes.clear();
}

function irA(view) {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent('deskte:navigate', { detail: { view } }));
  }
}

// ── Cotizar ────────────────────────────────────────────────────────────────

/** El cotizador del Mercado (ACA) no es un ramo: es su propia pantalla. */
export const COTIZADOR_MERCADO = 'mercado';
/** La asesoría guiada tampoco: abre la pestaña Asesorías. */
export const ENTREVISTA_GUIADA = 'entrevista';

/**
 * Lo que se le ofrece al agente cuando pulsa "Cotizar" en una ficha: el
 * cotizador del Mercado primero (es el que busca planes de verdad) y despues
 * cada ramo, que abre la cotizacion a mano con ese tipo de plan ya elegido.
 */
export function opcionesDeCotizacion(lang) {
  const es = lang === 'es';
  return [
    {
      id: COTIZADOR_MERCADO,
      label: es ? 'Mercado de Salud (ACA)' : 'Health Marketplace (ACA)',
      nota: es
        ? 'Busca planes reales con el ZIP, el ingreso y el hogar de la ficha'
        : "Searches live plans using the profile's ZIP, income and household",
    },
    // Segunda, justo detrás del Mercado (Lissette 2026-10-14: "mercado primero").
    {
      id: ENTREVISTA_GUIADA,
      label: es ? 'Asesoría guiada' : 'Guided consultation',
      nota: es
        ? 'Hogar, estatus, salud e ingresos paso a paso, con lo que se vende en su estado'
        : 'Household, status, health and income step by step, with what sells in their state',
    },
    // Con los tipos propios de la agencia al final (Wendy 2026-10-18).
    ...tiposDeCotizacion().map((p) => ({ id: p.v, label: es ? p.es : p.en, nota: '' })),
  ];
}

/**
 * El titulo con el que se abre la cotizacion. El titulo es OBLIGATORIO para
 * guardar (handleSubmit en QuotesInvoicesView), asi que llegar con uno escrito
 * es la diferencia entre guardar y que te rechace el formulario.
 */
export function tituloDeCotizacion(opcionId, cliente, lang) {
  const es = lang === 'es';
  const opcion = opcionesDeCotizacion(lang).find((o) => o.id === opcionId);
  const ramo = opcion && opcion.id !== COTIZADOR_MERCADO ? opcion.label : (es ? 'Salud' : 'Health');
  const nombre = [cliente?.firstName, cliente?.lastName].filter(Boolean).join(' ').trim();
  return nombre ? `${ramo} — ${nombre}` : ramo;
}

/**
 * Deja el pendiente y navega a Cotizaciones.
 * `opcionId` es COTIZADOR_MERCADO o un valor de `tiposDeCotizacion()`.
 */
export function irACotizar({ opcionId, cliente, lang, sinCredito = false }) {
  if (opcionId === ENTREVISTA_GUIADA) {
    dejarPendiente(PENDIENTE_ENTREVISTA, { clientId: cliente?.id || '' });
    irA('quotes_invoices');
    return;
  }
  const mercado = opcionId === COTIZADOR_MERCADO;
  dejarPendiente(PENDIENTE_COTIZAR, {
    clientId: cliente?.id || '',
    planType: mercado ? 'HEALTH' : opcionId,
    mercado,
    // Abrir el cotizador del Mercado SIN subsidio (Lissette 2026-10-14). La
    // asesoría ya cotiza el Mercado dentro (2026-10-15), pero el camino sigue
    // disponible para quien llegue aquí con un hogar sin crédito.
    sinCredito: mercado && !!sinCredito,
    titulo: tituloDeCotizacion(opcionId, cliente, lang),
  });
  irA('quotes_invoices');
}

// ── Firmas ─────────────────────────────────────────────────────────────────

const ICONO_DE_CATEGORIA = {
  consent: '✍️',
  hipaa: '🔒',
  soa: '📋',
  enrollment: '📄',
  communication: '📱',
  plan_change: '🔄',
  custom: '📝',
};

/** El nombre de la plantilla en el idioma de la pantalla. */
export function nombreDePlantilla(tpl, lang) {
  if (!tpl) return '';
  return (lang === 'es' ? tpl.name : (tpl.nameEn || tpl.name)) || tpl.name || '';
}

/**
 * Las plantillas que se le ofrecen al agente, ordenadas por nombre. No se
 * filtra ninguna: una plantilla propia de la agencia vale igual que una del
 * sistema, y esconderla seria esconder trabajo que ella ya hizo.
 */
export function opcionesDePlantilla(docTemplates, lang) {
  const es = lang === 'es';
  return (Array.isArray(docTemplates) ? docTemplates : [])
    .filter((t) => t && t.id)
    .map((t) => ({
      id: t.id,
      label: nombreDePlantilla(t, lang) || (es ? '(sin nombre)' : '(untitled)'),
      icono: ICONO_DE_CATEGORIA[t.category] || '📝',
    }))
    .sort((a, b) => a.label.localeCompare(b.label, es ? 'es' : 'en', { sensitivity: 'base' }));
}

// ── HealthSherpa ───────────────────────────────────────────────────────────

/**
 * Deja el pendiente y navega a "Inscribir en HealthSherpa". Viaja solo lo que
 * esa pantalla enseña del cliente (nombre y estado); el resto lo pide ella al
 * servidor con el id, igual que cuando se elige en su buscador.
 */
export function irAInscribirEnHealthSherpa({ cliente }) {
  if (!cliente?.id) return;
  dejarPendiente(PENDIENTE_HEALTHSHERPA, {
    id: cliente.id,
    firstName: cliente.firstName || '',
    lastName: cliente.lastName || '',
    state: cliente.state || cliente.addressState || '',
  });
  irA('healthsherpa_inscribir');
}

/** Firmas sin pendiente: para cuando todavia no hay ninguna plantilla. */
export function irAFirmas() {
  irA('signatures');
}

/** Deja el pendiente y navega a Firmas. */
export function irAFirmar({ templateId, cliente }) {
  dejarPendiente(PENDIENTE_FIRMA, {
    clientId: cliente?.id || '',
    templateId,
  });
  irA('signatures');
}
